/**
 * Persistencia ligera del historial del agente en localStorage.
 *
 * Guarda los últimos N mensajes para que el usuario vea la conversación al
 * volver al sitio. El `sessionId` se regenera si han pasado más de 24h desde
 * el último mensaje — n8n mantiene memoria por sessionId, así que reciclarlo
 * fuerza un contexto limpio cada día.
 */

const STORAGE_KEY = "caster-manager.agent.history";
const MAX_MESSAGES = 50;
const SESSION_TTL_MS = 24 * 60 * 60 * 1000;

export type AgentRole = "user" | "assistant";

export interface AgentMessage {
  role: AgentRole;
  content: string;
  ts: number;
}

export interface AgentState {
  sessionId: string;
  messages: AgentMessage[];
  lastActivity: number;
}

function newSessionId(): string {
  return `sess-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function freshState(): AgentState {
  return { sessionId: newSessionId(), messages: [], lastActivity: Date.now() };
}

export function loadState(): AgentState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return freshState();
    const parsed = JSON.parse(raw) as AgentState;
    if (
      !parsed?.sessionId ||
      !Array.isArray(parsed.messages) ||
      typeof parsed.lastActivity !== "number"
    ) {
      return freshState();
    }
    if (Date.now() - parsed.lastActivity > SESSION_TTL_MS) {
      return { ...freshState(), messages: parsed.messages.slice(-MAX_MESSAGES) };
    }
    return parsed;
  } catch {
    return freshState();
  }
}

export function saveState(state: AgentState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* localStorage full or disabled — silently drop */
  }
}

export function appendMessage(
  state: AgentState,
  role: AgentRole,
  content: string,
): AgentState {
  const next: AgentState = {
    sessionId: state.sessionId,
    messages: [...state.messages, { role, content, ts: Date.now() }].slice(
      -MAX_MESSAGES,
    ),
    lastActivity: Date.now(),
  };
  saveState(next);
  return next;
}

export function resetState(): AgentState {
  const next = freshState();
  saveState(next);
  return next;
}
